
import { StationService } from './../core/services/station.service';
import { StationSource } from './../core/models/station-source';
import { TimezoneEnum } from './../shared/model/time-zone.enum';
import { Router } from '@angular/router';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MultiDropboxData, MultiDropboxComponent } from '../shared';
import { ProfileModelService, Filter} from './profile-model.service';
import { Option } from 'src/app/shared/model/option';
import { SearchFiltersService, SearchFilterAggregate } from '../search-filters/search-filters.service';
import { BaseSearchDialogComponent } from '../search-dialog/base-search-dialog.component';
import { DialogData, Keyword } from '../search-dialog/search-dialog.component';
import { Station } from '../core';
import { forkJoin, Observable, BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ProfileService } from './profile.service';
import { ConfirmationDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';

export interface DefaultFilter {
  keywords: Keyword[];
  markets: MultiDropboxData[];
  formats: MultiDropboxData[];
  stations: MultiDropboxData[];
  originalMarketList: SearchFilterAggregate[];
  originalFormatList: SearchFilterAggregate[];
  originalStationList: Station[];
}

enum FilterType {
  HOME_DEFAULT_FILTER,
  SEARCH_DEFAULT_FILTER
}


@Component({

  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  providers: [SearchFiltersService]

})
export class ProfileComponent extends BaseSearchDialogComponent implements OnInit {

  @ViewChild('marketMulti', { static: true }) marketMulti: MultiDropboxComponent;
  @ViewChild('defaultHomeFilterMulti', { static: true }) defaultHomeFilterMulti: MultiDropboxComponent;
  @ViewChild('defaultSearchFilterMulti', { static: true }) defaultSearchFilterMulti: MultiDropboxComponent;

  updateButtonText = 'SAVE';
  cancelButtonText = 'CANCEL';

  defaultMarkets: MultiDropboxData[] = [];
  defaultHomeStationFilter: MultiDropboxData;
  defaultSearchFilter: MultiDropboxData;

  initialDefaultMarkets: MultiDropboxData[] = [];
  initialDefaultTimezoneType: TimezoneEnum;
  
  initialHomeStationFilter: MultiDropboxData = null;
  initialSearchFilter: MultiDropboxData = null;
  selectable = true;

  defaultTimezoneTypeOptions: Option<TimezoneEnum>[] = TimezoneEnum.getOptions();
  selectedDefaultTimezoneType: Option<TimezoneEnum>;

  defaultStationSources: StationSource[];
  profileDefaultStationSource: StationSource;
  selectedDefaultStationSource: StationSource;

  constructor(
    private router: Router,
    private searchFiltersService: SearchFiltersService,
    dialog: MatDialog,
    profileService: ProfileService,
    private titleService: Title,
    private stationService: StationService,
    profileModelService: ProfileModelService) {
    super(dialog, profileService, profileModelService);
    this.titleService.setTitle('SIA (Search, Index, Archive) - User Settings');
  }

  ngOnInit() {
    this.stationService.stationSourceList().subscribe(list => {
      this.defaultStationSources = list;
      // Reset object, as the database station source property does not accept a null value
      this.defaultStationSources.unshift({id: -1, name: 'None', shortname: 'None'});
      this.loadDefaultStationSource();
    });

    this.loadMarketList();
    this.loadDefaultTimezoneType();
    this.loadHomePageDefaultFilterList();
    this.loadSearchPageDefaultFilterList();
    this.resetFilters();
  }

  private loadMarketList() {
    this.searchFiltersService.loadAllMarkets().subscribe((markets: SearchFilterAggregate[]) => {
      this.marketMulti.setList(markets.map((marketFilter: SearchFilterAggregate) => {
        return {
          id: marketFilter.aggregate,
          name: marketFilter.name
        };
      }));
    });
  }

  private loadDefaultTimezoneType() {
    this.initialDefaultTimezoneType = this.profileModelService.getDefaultTimezoneType();
    this.selectedDefaultTimezoneType = this.defaultTimezoneTypeOptions.find(
      (element) => element.value === this.initialDefaultTimezoneType);
  }

  private loadDefaultStationSource() {
    this.profileDefaultStationSource = this.profileModelService.getDefaultStationSource();
    if (this.profileDefaultStationSource) {
      this.selectedDefaultStationSource = this.defaultStationSources.find(
        (element) => element.id === this.profileDefaultStationSource.id);
    }
  }

  private loadHomePageDefaultFilterList() {
    const savedFilterStations: Filter[] = this.profileModelService.getStationFilters() || [];
    this.defaultHomeFilterMulti.setList(savedFilterStations.map((savedFilterStation: Filter) => {
      return {
        id: savedFilterStation.filterName,
        name: savedFilterStation.filterName
      };
    }));
  }

  private loadSearchPageDefaultFilterList() {
    const savedFilterSearch: Filter[] = this.profileModelService.getSearchFilter() || [];
    this.defaultSearchFilterMulti.setList(savedFilterSearch.map((savedFilter: Filter) => {
      return {
        id: savedFilter.filterName,
        name: savedFilter.filterName
      };
    }));
  }

  editDefaultSearchFilter(item: MultiDropboxData): void {
    const filterName: string = item.id;
    const transcriptFilter: Filter = this.profileModelService.getSearchFilter()
      .find((filter: Filter) => filter.filterName === filterName);

    if (transcriptFilter) {
      this.buildFilterData(transcriptFilter.keywords, transcriptFilter.markets, [], transcriptFilter.stations)
        .subscribe((defaultFilter: DefaultFilter) => {
          if (defaultFilter) {
            this.openEditDialogFilter({
              filterName: transcriptFilter.filterName,
              oldFilterName: transcriptFilter.filterName,
              stations: [].concat(defaultFilter.stations),
              markets: [].concat(defaultFilter.markets),
              keywords: [].concat(defaultFilter.keywords),
              origMarketsList: [].concat(defaultFilter.originalMarketList),
              origStationsList: [].concat(defaultFilter.originalStationList),
              isHomePage: false
            }, FilterType.SEARCH_DEFAULT_FILTER);
          }
        });
    }
  }

  editDefaultHomeFilter(item: MultiDropboxData): void {
    const filterName: string = item.id;
    const homeFilter: Filter = this.profileModelService.getStationFilters()
      .find((filter: Filter) => filter.filterName === filterName);

    if (homeFilter) {
      this.buildFilterData([], homeFilter.markets, homeFilter.formats, homeFilter.stations)
        .subscribe((defaultFilter: DefaultFilter) => {
          if (defaultFilter) {
            this.openEditDialogFilter({
              filterName: homeFilter.filterName,
              oldFilterName: homeFilter.filterName,
              stations: [].concat(defaultFilter.stations),
              markets: [].concat(defaultFilter.markets),
              formats: [].concat(defaultFilter.formats),
              origMarketsList: [].concat(defaultFilter.originalMarketList),
              origStationsList: [].concat(defaultFilter.originalStationList),
              origFormatsList: [].concat(defaultFilter.originalFormatList),
              isHomePage: true
            }, FilterType.HOME_DEFAULT_FILTER);
          }
        });
    }
  }

  buildFilterData(keywordIds: string[], marketIds: string[], formatIds: string[],
    stationIds: String[]): Observable<DefaultFilter> {
    const _defaultFilter: BehaviorSubject<DefaultFilter> = <BehaviorSubject<DefaultFilter>>new BehaviorSubject(null);
    const defaultFilter = _defaultFilter.asObservable();

    let keywords: Keyword[];
    let markets: MultiDropboxData[];
    let formats: MultiDropboxData[];
    let stations: MultiDropboxData[];
    let marketList: SearchFilterAggregate[];
    let formatList: SearchFilterAggregate[];
    let stationList: Station[];

    forkJoin([
      this.searchFiltersService.loadAllMarkets(),
      this.searchFiltersService.loadAllFormats(),
      this.searchFiltersService.loadAllStations()
    ]).subscribe(
      (values) => {
        marketList = values[0];
        formatList = values[1];
        stationList = values[2];

        markets = marketList.filter((marketFilter: SearchFilterAggregate) => marketIds.indexOf(marketFilter.name) >= 0)
          .map((marketFilter: SearchFilterAggregate) => {
            return {
              id: marketFilter.aggregate,
              name: marketFilter.name,
            } as MultiDropboxData;
          });

        formats = formatList.filter((formatFilter: SearchFilterAggregate) => formatIds.indexOf(formatFilter.name) >= 0)
          .map((formatFilter: SearchFilterAggregate) => {
            return {
              id: formatFilter.name,
              name: formatFilter.name,
              model: formatFilter
            } as MultiDropboxData;
          });

        stations = stationList.filter((station: Station) => stationIds.indexOf(station.id) >= 0)
          .map((station: Station) => {
            const name = station.name ? station.name : '';
            const frequency = station.frequency ? station.frequency : '';
            return {
              id: station.id,
              name: `${name} ${frequency}`,
              model: station
            } as MultiDropboxData;
          });
      },
      (error) => {
        // TODO: Implement the error popup or something to display the issue on load the filters
        console.error(error);
      },

      () => {
        keywords = keywordIds.map((keyword: string) => {
          return { name: keyword } as Keyword;
        });

        _defaultFilter.next({
          keywords: keywords || [],
          stations: stations || [],
          formats: formats || [],
          markets: markets || [],
          originalMarketList: marketList || [],
          originalFormatList: formatList || [],
          originalStationList: stationList || []
        } as DefaultFilter);

      }
    );

    return defaultFilter;
  }

  saveDefault(): void {
    const homeFilterName: string = this.defaultHomeStationFilter ? this.defaultHomeStationFilter.id : 'null';
    const searchFilterName: string = this.defaultSearchFilter ? this.defaultSearchFilter.id : 'null';
    const defaultMarket = this.defaultMarkets != null ? this.defaultMarkets.reduce(
      (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
    ) : [];
    const defaultTimezone = this.selectedDefaultTimezoneType.value;
    const defaultStationSource = this.selectedDefaultStationSource;

    this.profileModelService.setDefaultMarkets(defaultMarket);
    this.profileModelService.setDefaultStationFilter(homeFilterName);
    this.profileModelService.setDefaultSearchFilter(searchFilterName);
    this.profileModelService.setDefaultTimezoneType(defaultTimezone);
    this.profileModelService.setDefaultStationSource(defaultStationSource);

    this.profileService.updateConfiguration(this.profileModelService.configuration).subscribe(
      (profile) => {
        this.profileModelService.profile = profile;
        const dialogConfirmRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '400px',
          data: {
            'title': 'Notification',
            'text': 'You have Saved the User Settings',
            'hideCancel': true,
            'okText': 'OK'
          }
        });
        dialogConfirmRef.afterClosed().subscribe(result => {
          if (!!result) {
            this.router.navigate(['home']);
          }
        });
      }
    );
  }

  get disableClearButton(): boolean {
    return !(this.defaultHomeStationFilter != null
      || this.defaultSearchFilter != null
      || (this.defaultMarkets != null && this.defaultMarkets.length > 0));
  }

  private resetFilters(): void {
    const stationFilter: Filter = this.profileModelService.getDefaultStationFilter();
    const transcriptFilter: Filter = this.profileModelService.getDefaultSearchFilter();

    if (stationFilter.filterName) {
      this.defaultHomeStationFilter = {
        id: stationFilter.filterName,
        name: stationFilter.filterName
      } as MultiDropboxData;
      this.initialHomeStationFilter = { ...this.defaultHomeStationFilter };
    } else {
      this.defaultHomeStationFilter = null;
    }

    if (transcriptFilter.filterName) {
      this.defaultSearchFilter = {
        id: transcriptFilter.filterName,
        name: transcriptFilter.filterName
      } as MultiDropboxData;
      this.initialSearchFilter = { ...this.defaultSearchFilter };
    } else {
      this.defaultSearchFilter = null;
    }

    this.searchFiltersService.loadAllMarkets().subscribe((markets: SearchFilterAggregate[]) => {
      this.defaultMarkets = markets
        .filter(
          (market: SearchFilterAggregate) =>
            market.aggregate.filter(id => new Set(this.profileModelService.getDefaultMarkets()).has(id)).length > 0
        )
        .map((marketFilter: SearchFilterAggregate) => {
          if (marketFilter) {
            return {
              id: marketFilter.aggregate,
              name: marketFilter.name
            };
          }

          return null;
        });
      this.initialDefaultMarkets = [...this.defaultMarkets];
    });
  }

  resetFiltersWithDialog(): void {
    const dialogConfirmRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        'text': 'Are you sure you want to leave the User Settings page without saving?'
      }
    });
    dialogConfirmRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.resetFilters();
        this.router.navigate(['home']);
      }
    });
  }

  clearAll() {
    this.defaultMarkets = null;
    this.defaultHomeStationFilter = null;
    this.defaultSearchFilter = null;
  }

  removeDefaultMarketFilter(marketFilter: MultiDropboxData) {
    this.defaultMarkets = this.defaultMarkets.filter(item => item !== marketFilter);
  }

  removeDefaultHomeFilter() {
    this.defaultHomeStationFilter = null;
  }

  removeDefaultSearchFilter() {
    this.defaultSearchFilter = null;
  }

  dialogUpdate(dialogData: DialogData, type: FilterType): void {
    if (type === FilterType.HOME_DEFAULT_FILTER) {
      this.profileModelService.addStationFilter({
        formats: dialogData.formats.map((formatMulti: MultiDropboxData) => formatMulti.name),
        markets: dialogData.markets.map((marketMulti: MultiDropboxData) => marketMulti.name),
        stations: dialogData.stations.map((stationMulti: MultiDropboxData) => stationMulti.id),
        filterName: dialogData.filterName
      });
    } else {
      this.profileModelService.addSearchFilter({
        keywords: dialogData.keywords.map((keyword: Keyword) => keyword.name),
        markets: dialogData.markets.map((marketMulti: MultiDropboxData) => marketMulti.name),
        stations: dialogData.stations.map((stationMulti: MultiDropboxData) => stationMulti.id),
        filterName: dialogData.filterName
      });
    }
  }

  afterSavedDialog(filterName: string, type: FilterType): void {
    this.loadHomePageDefaultFilterList();
    this.loadSearchPageDefaultFilterList();
    if (type === FilterType.HOME_DEFAULT_FILTER) {
      this.defaultHomeStationFilter = {
        id: filterName,
        name: filterName
      } as MultiDropboxData;
    } else {
      this.defaultSearchFilter = {
        id: filterName,
        name: filterName
      } as MultiDropboxData;
    }
  }

  get disableSaveBtn(): boolean {
    return this.arrayEqual(this.initialDefaultMarkets, this.defaultMarkets)
      && JSON.stringify(this.initialHomeStationFilter) === JSON.stringify(this.defaultHomeStationFilter)
      && JSON.stringify(this.initialSearchFilter) === JSON.stringify(this.defaultSearchFilter)
      && this.initialDefaultTimezoneType === this.selectedDefaultTimezoneType.value
      && this.profileDefaultStationSource?.id === this.selectedDefaultStationSource?.id;
  }

  arrayEqual = (arr1, arr2): boolean => {
    if (!arr1 && !arr2) {
      return true;
    }

    if ((arr1 && !arr2 && (arr1.length === 0)) || (arr2 && !arr1 && (arr2.length === 0))) {
      return true;
    }

    if ((arr1 && !arr2) || (arr2 && !arr1)) {
      return false;
    }

    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = arr1.length; i--;) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }

  clearFilters() { }

}
